@if (workspace.customFields.length > 0 && (customFields | keyvalue).length > 0) {
  <table aria-label="Custom fields table" class="settingsTable">
    <thead class="customFieldTable__head">
      <tr class="customFieldTable__row">
        <th data-test="siteSettingsColumnName" scope="col" class="customFieldTable__header">
          {{ 'name' | ppTranslate }}
        </th>

        <th
          data-test="siteSettingsColumnVisibility"
          scope="col"
          class="customFieldTable__header--center"
        >
          {{ 'visibility' | ppTranslate }}

          <pp-icon
            [ppSrc]="EIconPath.ICON_BADGE_INFO"
            ppColor="grey-600"
            ppClass="setSize14"
            ppTooltip
            [ppTitle]="'custom_field_visibility_tooltip' | ppTranslate"
          />
        </th>

        <th
          data-test="siteSettingsColumnEditability"
          scope="col"
          class="customFieldTable__header--center"
        >
          {{ 'editability' | ppTranslate }}

          <pp-icon
            [ppSrc]="EIconPath.ICON_BADGE_INFO"
            ppColor="grey-600"
            ppClass="setSize14"
            ppTooltip
            [ppTitle]="'locks_custom_fields' | ppTranslate"
          />
        </th>

        <th data-test="siteSettingsColumnType" scope="col" class="customFieldTable__header--center">
          {{ 'type' | ppTranslate }}
        </th>

        <th data-test="siteSettingsColumnActions" scope="col" class="customFieldTable__header--end">
          {{ 'actions' | ppTranslate }}
        </th>
      </tr>
    </thead>

    @if (onDragOverIndex !== null) {
      <div
        class="settingsTable__line--wrapper"
        [style.marginTop.px]="linePosition"
        [class.dragging]="dragging"
      >
        <div class="settingsTable__line" [class.dragging]="dragging"></div>
      </div>
    }

    @for (fieldId of workspace.customFields; track fieldId; let index = $index) {
      @if (customFields[ppWorkspaceId]?.[fieldId] && !hiddenCustomFields.includes(fieldId)) {
        <tbody
          class="settingsTable__item-grab"
          [class.settingsTable__item--opacity]="draggedCustomFieldId === fieldId"
          (dragover)="onDragOver($event, index, fieldId)"
          (dragenter)="$event.preventDefault()"
          (dragleave)="$event.preventDefault()"
          (drop)="onDrop($event)"
          (dragend)="onDragEnd()"
        >
          <tr
            id="{{ 'itemRow-' + fieldId }}"
            class="customFieldTable__row"
            draggable="true"
            (dragstart)="onDragStart($event, fieldId, index)"
          >
            <td class="customFieldTable__cell">
              <div class="settingsTable__cell--row" [class.dragging]="dragging">
                <div
                  data-m-target="Custom Field Reorder Button"
                  class="settingsTable__drag-icon"
                ></div>

                <span
                  class="settingsTable__cell--fieldName"
                  ppTooltip
                  [ppTitle]="customFields[ppWorkspaceId][fieldId]?.description"
                  [ppOptions]="{ placement: 'right' }"
                >
                  {{ customFields[ppWorkspaceId][fieldId]?.label }}
                </span>
              </div>
            </td>

            <td class="customFieldTable__cell">
              <div class="settingsTable__cell--row" [class.dragging]="dragging">
                @if (
                  customFields[ppWorkspaceId][fieldId].volyIntegrationActive !==
                  EIntegrationStatus.ON
                ) {
                  <pp-icon
                    data-test="visibilityIcon"
                    ppTooltip
                    [ppTitle]="
                      (customFields[ppWorkspaceId][fieldId]?.display
                        ? 'custom_field_visible_tooltip'
                        : 'custom_field_hidden_tooltip'
                      ) | ppTranslate
                    "
                    [ppSrc]="
                      customFields[ppWorkspaceId][fieldId]?.display
                        ? EIconPath.ICON_USER_GROUPS_VISIBLE_24
                        : EIconPath.ICON_USER_GROUPS_HIDDEN_24
                    "
                    ppClass="setSize24"
                    ppColor="grey-900"
                    (click)="toggleVisibility(fieldId)"
                    class="settingsTable__visibilityIcon"
                  />
                }
              </div>
            </td>

            <td class="customFieldTable__cell" [class.dragging]="dragging">
              @if (isCustomFieldLockable(fieldId)) {
                <pp-icon
                  data-test="editabilityIcon"
                  ppTooltip
                  [ppTitle]="
                    (customFields[ppWorkspaceId][fieldId].lockedValue
                      ? 'custom_field_is_locked'
                      : 'custom_field_is_editable'
                    ) | ppTranslate
                  "
                  [ppSrc]="
                    customFields[ppWorkspaceId][fieldId].lockedValue
                      ? EIconPath.PADLOCK_CLOSED
                      : EIconPath.PADLOCK_OPEN
                  "
                  ppClass="setSize24"
                  ppColor="grey-900"
                  (click)="toggleLockedSite(fieldId)"
                  class="settingsTable__visibilityIcon"
                />
              }
            </td>

            <td class="customFieldTable__cell--center" [class.dragging]="dragging">
              <div class="settingsTable__icon--wrapper">
                <img
                  ppImage
                  class="settingsTable__icon settingsTable__icon--type"
                  [src]="getCustomFieldIconSrc(customFields[ppWorkspaceId][fieldId])"
                />
              </div>

              <span class="settingsTable__type">
                {{
                  volyCustomFieldLabel[
                    customFields[ppWorkspaceId][fieldId]?.volyIntegrationActive
                  ] || customFieldLabels[customFields[ppWorkspaceId][fieldId]?.type]
                }}
              </span>
            </td>

            <td class="customFieldTable__cell--end">
              <pp-button
                ppTooltip
                [ppTitle]="
                  customFields[ppWorkspaceId][fieldId]?.lockedTemplate
                    ? 'This custom field is read only'
                    : ''
                "
                ppStyle="primary"
                [class.dragging]="dragging"
                ppId="{{ 'siteSettingsCFOptions' + index }}"
                (ppAction)="editCustomField(fieldId)"
                ppSize="small"
                [ppDisabled]="customFields[ppWorkspaceId][fieldId]?.lockedTemplate"
              >
                <pp-icon
                  [ppSrc]="EIconPath.ICON_MISC_EDIT_14"
                  ppColor="white"
                  ppClass="ml--n04 setSize14"
                />
                {{ 'edit' | ppTranslate }}
              </pp-button>

              <pp-delete-button
                ppTooltip
                [ppTitle]="getRemoveTooltipTitle(customFields[ppWorkspaceId][fieldId])"
                [ppId]="'siteSettingsCFDelete' + index"
                [ppDisabled]="customFields[ppWorkspaceId][fieldId]?.lockedTemplate"
                [ppDragging]="dragging"
                (ppAction)="deleteCustomField(fieldId)"
              />
            </td>
          </tr>
        </tbody>
      }
    }
  </table>
}
